import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Message, Session } from '../models/chat.models';

@Injectable({
  providedIn: 'root'
})
export class ChatStateService {
  // Dictionary to hold messages per session ID
  private messagesDictSubject = new BehaviorSubject<Record<string, Message[]>>({});
  
  // Dictionary to hold loading state per session ID
  private loadingDictSubject = new BehaviorSubject<Record<string, boolean>>({});

  private idSubject = new BehaviorSubject<string | null>(null);
  public id$: Observable<string | null> = this.idSubject.asObservable();

  // We derive the active messages from the active session ID and the dict
  private activeMessagesSubject = new BehaviorSubject<Message[]>([]);
  public messages$: Observable<Message[]> = this.activeMessagesSubject.asObservable();

  // We derive the active loading state from active session
  private activeLoadingSubject = new BehaviorSubject<boolean>(false);
  public isLoading$: Observable<boolean> = this.activeLoadingSubject.asObservable();

  private sessionsSubject = new BehaviorSubject<Session[]>([]);
  public sessions$: Observable<Session[]> = this.sessionsSubject.asObservable();

  private isGraphLoadingSubject = new BehaviorSubject<boolean>(false);
  public isGraphLoading$: Observable<boolean> = this.isGraphLoadingSubject.asObservable();

  private errorSubject = new BehaviorSubject<string | null>(null);
  public error$: Observable<string | null> = this.errorSubject.asObservable();

  private successMessageSubject = new BehaviorSubject<string | null>(null);
  public successMessage$: Observable<string | null> = this.successMessageSubject.asObservable();

  constructor() {
    // When session ID changes, update active streams
    this.id$.subscribe(id => {
      this.updateActiveObservables(this.idSubject.value, this.messagesDictSubject.value, this.loadingDictSubject.value);
    });

    this.messagesDictSubject.subscribe(dict => {
      this.updateActiveObservables(this.idSubject.value, dict, this.loadingDictSubject.value);
    });

    this.loadingDictSubject.subscribe(dict => {
      this.updateActiveObservables(this.idSubject.value, this.messagesDictSubject.value, dict);
    });
  }

  private updateActiveObservables(
    id: string | null,
    messagesDict: Record<string, Message[]>,
    loadingDict: Record<string, boolean>
  ) {
    const key = id || 'new';
    this.activeMessagesSubject.next(messagesDict[key] || []);
    this.activeLoadingSubject.next(!!loadingDict[key]);
  }

  // --- Messages ---
  getMessages(): Message[] {
    return this.activeMessagesSubject.value;
  }

  getMessagesForSession(id: string): Message[] {
    return this.messagesDictSubject.value[id] || [];
  }

  setMessages(id: string, messages: Message[]): void {
    const dict = { ...this.messagesDictSubject.value };
    dict[id] = messages;
    this.messagesDictSubject.next(dict);
  }

  addMessage(id: string, message: Message): void {
    const dict = { ...this.messagesDictSubject.value };
    const current = dict[id] || [];
    dict[id] = [...current, message];
    this.messagesDictSubject.next(dict);
  }

  updateLastMessage(id: string, updater: (msg: Message) => Message): void {
    const dict = { ...this.messagesDictSubject.value };
    const current = dict[id] || [];
    if (current.length > 0) {
      const updated = [...current];
      updated[updated.length - 1] = updater(updated[updated.length - 1]);
      dict[id] = updated;
      this.messagesDictSubject.next(dict);
    }
  }
 
  removeLastMessage(id: string): void {
    const dict = { ...this.messagesDictSubject.value };
    const current = dict[id] || [];
    if (current.length > 0) {
      dict[id] = current.slice(0, -1);
      this.messagesDictSubject.next(dict);
    }
  }

  clearMessages(id: string): void {
    const dict = { ...this.messagesDictSubject.value };
    dict[id] = [];
    this.messagesDictSubject.next(dict);
  }

  // --- Session ---
  get id(): string | null {
    return this.idSubject.value;
  }

  setSessionId(id: string | null): void {
    this.idSubject.next(id);
  }

  // --- Sessions List ---
  getSessions(): Session[] {
    return this.sessionsSubject.value;
  }

  setSessions(sessions: Session[]): void {
    this.sessionsSubject.next(sessions);
  }

  addSession(session: Session): void {
    const current = this.sessionsSubject.value;
    this.sessionsSubject.next([session, ...current]);
  }

  removeSession(id: string): void {
    // Also cleanup dictionaries
    const msgsDict = { ...this.messagesDictSubject.value };
    delete msgsDict[id];
    this.messagesDictSubject.next(msgsDict);

    const loadingDict = { ...this.loadingDictSubject.value };
    delete loadingDict[id];
    this.loadingDictSubject.next(loadingDict);

    const current = this.sessionsSubject.value;
    this.sessionsSubject.next(current.filter(s => s.id !== id));
  }

  updateSessionTitle(id: string, title: string): void {
    const currentSessions = this.sessionsSubject.value;
    const updated = currentSessions.map(s => 
      s.id === id ? { ...s, title } : s
    );
    this.sessionsSubject.next(updated);
  }

  transferSessionState(oldSessionId: string, newSessionId: string): void {
    const msgsDict = { ...this.messagesDictSubject.value };
    if (msgsDict[oldSessionId]) {
      msgsDict[newSessionId] = msgsDict[oldSessionId];
      delete msgsDict[oldSessionId];
      this.messagesDictSubject.next(msgsDict);
    }

    const loadingDict = { ...this.loadingDictSubject.value };
    if (loadingDict[oldSessionId] !== undefined) {
      loadingDict[newSessionId] = loadingDict[oldSessionId];
      delete loadingDict[oldSessionId];
      this.loadingDictSubject.next(loadingDict);
    }
  }

  // --- Loading States ---
  setLoading(id: string, loading: boolean): void {
    const dict = { ...this.loadingDictSubject.value };
    dict[id] = loading;
    this.loadingDictSubject.next(dict);
  }

  getLoadingForSession(id: string): boolean {
    return !!this.loadingDictSubject.value[id];
  }

  setGraphLoading(loading: boolean): void {
    this.isGraphLoadingSubject.next(loading);
  }

  // --- Notifications ---
  setError(error: string | null): void {
    this.errorSubject.next(error);
    if (error) {
      this.successMessageSubject.next(null);
    }
  }

  setSuccessMessage(message: string | null): void {
    this.successMessageSubject.next(message);
    if (message) {
      this.errorSubject.next(null);
    }
  }

  clearNotifications(): void {
    this.errorSubject.next(null);
    this.successMessageSubject.next(null);
  }
}
